import { useState, useEffect, useCallback, useRef } from "react";
import * as Sentry from "../lib/errorReporting";
import { apiFetch } from "../lib/api";

/**
 * League-scoped odds fetcher. Triggers /api/nhl-odds on NHL LIVE game
 * transitions only. MLB LIVE games are a deliberate no-op -- no MLB odds
 * endpoint exists. Returns empty oddsData for non-NHL leagues.
 * @param {Object} params
 * @param {Array} params.games - games array from useGameCacheRealtime
 * @returns {{ oddsData: Array, loadOdds: Function }}
 */
export function useOdds({ games }) {
  const [oddsData, setOddsData] = useState([]);
  const hadLiveGamesRef = useRef(false);

  const loadOdds = useCallback(async () => {
    try {
      const res = await apiFetch("/api/nhl-odds");
      if (!res.ok) return;
      const data = await res.json();
      if (data.games) setOddsData(data.games);
    } catch (err) {
      Sentry.captureException(err, {
        tags: { function: 'loadOdds', failureStage: 'odds-fetch', league: 'nhl' },
      });
    }
  }, []);

  // odds: game-aware trigger only — no interval polling
  // reinstated v1.0.168 with budget governor
  useEffect(() => {
    const hasLive = games.some(g => g.status === "LIVE" && g.league === "nhl");
    if (hasLive && !hadLiveGamesRef.current) {
      loadOdds();
    }
    hadLiveGamesRef.current = hasLive;
  }, [games, loadOdds]); // eslint-disable-line react-hooks/exhaustive-deps

  return { oddsData, loadOdds };
}
